import React, { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActionList, Avatar, Box, Button, Icon } from '@node-space/ui-components'
import { ConnectionStatusTypes, NetworkConnectionViewTypes } from 'constants/bvnkNetwork'
import { BvnkNetworkConnection, ConnectionStatusType } from 'types/bvnkNetwork'
import { ConnectionConfirmationModal } from './ConnectionConfirmationModal'
import ConnectionsEmptyState from './ConnectionsEmptyState'

export interface IncomingPendingConnectionsProps {
  incomingPendingConnections: BvnkNetworkConnection[]
  isUpdatingConnection: boolean
  updateConnection: (
    connection: BvnkNetworkConnection,
    connectionStatus: ConnectionStatusType
  ) => void
}

const IncomingPendingConnections = ({
  incomingPendingConnections,
  isUpdatingConnection,
  updateConnection,
}: IncomingPendingConnectionsProps) => {
  const { t } = useTranslation()

  const [connectionToUpdate, setConnectionToUpdate] = useState<BvnkNetworkConnection>()
  const [updateType, setUpdateType] = useState<ConnectionStatusType>()
  const [isRejectConnectionModalOpen, setIsRejectConnectionModalOpen] = useState(false)
  const [connectionToReject, setConnectionToReject] = useState<BvnkNetworkConnection>(null)

  const handleConnectionAccept = (connection: BvnkNetworkConnection) => {
    setConnectionToUpdate(connection)
    setUpdateType(ConnectionStatusTypes.CONNECTED)
    updateConnection(connection, ConnectionStatusTypes.CONNECTED)
  }

  const handleConnectionReject = (connection: BvnkNetworkConnection) => {
    setConnectionToReject(connection)
    setIsRejectConnectionModalOpen(true)
  }

  const onRejectConnectionModalClose = () => {
    setConnectionToReject(null)
    setIsRejectConnectionModalOpen(false)
  }

  const actionItemListItems = useMemo(() => {
    return incomingPendingConnections?.map(connection => {
      return {
        transparent: false,
        testId: `incoming-pending-connection-list-item-${connection?.id}`,
        id: connection?.id,
        key: connection?.id,
        Avatar: <Avatar name={connection?.details?.name} size="small" type="entity" />,
        title: connection?.details?.name,
        BodyPost: connection?.details?.description,
        ActionButton: (
          <Button
            variant="ghost"
            key={`connection-${connection?.id}-accept-button`}
            iconElement={<Icon name="Check" size="small" />}
            onClick={() => handleConnectionAccept(connection)}
            loading={
              isUpdatingConnection &&
              connectionToUpdate?.id === connection?.id &&
              updateType === ConnectionStatusTypes.CONNECTED
            }
          >
            {t('bvnkNetwork.accept')}
          </Button>
        ),
        CustomAction: (
          <Button
            variant="destructive-ghost"
            key={`connection-${connection?.id}-reject-button`}
            iconElement={<Icon name="Prohibit" size="small" />}
            onClick={() => handleConnectionReject(connection)}
            loading={
              isUpdatingConnection &&
              connectionToUpdate?.id === connection?.id &&
              updateType === ConnectionStatusTypes.DISCONNECTED
            }
          >
            {t('bvnkNetwork.deny')}
          </Button>
        ),
      }
    })
  }, [incomingPendingConnections, isUpdatingConnection, connectionToUpdate, updateType])

  if (!actionItemListItems?.length) {
    return (
      <ConnectionsEmptyState
        networkConnectionViewType={NetworkConnectionViewTypes.RECEIVED_INVITATIONS_VIEW}
      />
    )
  } else {
    return (
      <Box flexCol>
        <ActionList id="connections-list" items={actionItemListItems} />
        {isRejectConnectionModalOpen && (
          <ConnectionConfirmationModal
            connection={connectionToReject}
            connectionStatus={ConnectionStatusTypes.REJECTED}
            onModalClose={onRejectConnectionModalClose}
          />
        )}
      </Box>
    )
  }
}

export default memo(IncomingPendingConnections)
